import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';

export interface MultiSelectOption {
  id: string;
  text: string;
}

// SearchFunc can be used to filter options on fields other than optionText
export type SearchFunc = (searchTerm: string, option: any) => boolean;

import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';

@Component({
  selector: 'va-multi-select',
  templateUrl: './va-multi-select.component.html',
  styleUrls: ['va-multi-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    MatIconModule,
    MatSelectModule,
    MatInputModule,
    GalaxyFormFieldModule,
    FormsModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatChipsModule,
  ],
})
export class VaMultiSelectComponent {
  @Output() selection = new EventEmitter<(MultiSelectOption | string)[]>();

  @Input() options: (MultiSelectOption | string)[];
  @Input() selected: (MultiSelectOption | string)[] = [];
  @Input() displayProperty: string;
  @Input() placeholderText: string;
  @Input() allowNew = false; // if true, will allow new values (not in options) to be selected
  @Input() limitResults = false; // constrain the number of results that are rendered into the option box
  @Input() searchFunc: SearchFunc; // if set this function will be used to search options instead of matching optionText

  inputControl = new FormControl<string>('');

  constructor(private cdr: ChangeDetectorRef) {}

  deselectOption(option: any): void {
    const index = this.selected.indexOf(option);
    if (index > -1) {
      this.selected.splice(index, 1);
      this.selection.emit(this.selected);
    }

    this.cdr.detectChanges();
  }

  optionSelected(event: any): void {
    // don't allow selection of duplicate items (we can potentially make this an option later)
    if (this.selected.indexOf(event.option.value) === -1) {
      this.selected.push(event.option.value);
      this.selection.emit(this.selected);
    }
    this.inputControl.setValue('');
  }

  optionText(option: any): string {
    return this.displayProperty ? option[this.displayProperty] : option;
  }

  optionTextPostMatch(option: any): string {
    // returns the portion of the optionText after a matching search term
    if (!this.inputControl.value) {
      return '';
    }
    const optionText = this.optionText(option);
    const index = optionText.toLowerCase().indexOf(this.inputControl.value.toLowerCase());
    return optionText.slice(index + this.inputControl.value.length);
  }

  optionTextMatch(option: any): string {
    // returns the portion of the optionText matching the search term
    if (!this.inputControl.value) {
      return '';
    }
    const optionText = this.optionText(option);
    const index = optionText.toLowerCase().indexOf(this.inputControl.value.toLowerCase());
    return optionText.slice(index, index + this.inputControl.value.length);
  }

  optionTextPreMatch(option: any): string {
    // returns the portion of the optionText before a matching search term
    const optionText = this.optionText(option);
    if (!this.inputControl.value) {
      return optionText;
    }
    const index = optionText.toLowerCase().indexOf(this.inputControl.value.toLowerCase());
    return optionText.slice(0, index);
  }

  get filteredOptions(): any[] {
    let displayedOptions: (MultiSelectOption | string)[];
    const options = this.options.filter((option) => !this.selected.find((selected) => selected === option));

    if (this.inputControl.value) {
      let filteredOptions: (MultiSelectOption | string)[];
      if (this.searchFunc) {
        filteredOptions = this.options.filter((option) => this.searchFunc(this.inputControl.value || '', option));
      } else {
        filteredOptions = this.options.filter(
          (option) =>
            this.optionText(option)
              .toLowerCase()
              .indexOf((this.inputControl.value || '').toLowerCase()) !== -1,
        );
      }
      if (this.allowNew) {
        if (
          filteredOptions
            .map((option) => this.optionText(option).toLowerCase())
            .indexOf(this.inputControl.value.toLowerCase()) === -1
        ) {
          // no exact match term found, so add a new one to the options
          filteredOptions.unshift(this.buildOption(this.inputControl.value));
        }
      }
      displayedOptions = filteredOptions;
    } else {
      displayedOptions = options;
    }

    if (this.limitResults) {
      displayedOptions = displayedOptions.slice(0, 50);
    }

    return displayedOptions;
  }

  private buildOption(name: string): any {
    // if using displayProperty, return an object containing just that, otherwise just the string itself
    if (this.displayProperty) {
      const option = {};
      option[this.displayProperty] = name;
      return option;
    }
    return name;
  }
}
